import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Armchair, Check, ArrowRight } from 'lucide-react';
import { displayFont, fadeInUp, staggerContainer } from './shared';
import { BrowserMockup, HeroAnimCanvas } from './TablePlanMockup';
import type { HomepageStrings } from '../../i18n/homepage';

type SeatingPlannerStrings = Pick<
  HomepageStrings,
  | 'seating_planner_badge'
  | 'seating_planner_h2'
  | 'seating_planner_body'
  | 'seating_planner_bullets'
  | 'seating_planner_cta_link'
  | 'seating_planner_price_label'
>;

const EN_DEFAULTS: SeatingPlannerStrings = {
  seating_planner_badge: 'Seating Planner',
  seating_planner_h2: 'Plan every seat.\nAll in one place.',
  seating_planner_body: 'Design your room layout on an interactive canvas, drag guests onto seats, and export a beautiful PDF — everything your venue needs, done before the wedding.',
  seating_planner_bullets: [
    'Drag tables anywhere — round, rectangular or square',
    'Assign guests to seats with drag-and-drop',
    'Dietary and allergy flags visible at a glance',
    'Export a print-ready PDF for your venue',
  ],
  seating_planner_cta_link: 'Learn about Seating Planner',
  seating_planner_price_label: '· One-time add-on',
};

export function SeatingPlannerSection({ strings }: { strings?: SeatingPlannerStrings }) {
  const s = strings ?? EN_DEFAULTS;
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className="py-24 px-6 bg-background-card border-y border-border">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

        {/* Left: text */}
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}>

          <motion.span
            variants={fadeInUp}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-6">
            <Armchair size={13} /> {s.seating_planner_badge}
          </motion.span>

          <motion.h2
            variants={fadeInUp}
            className="text-4xl sm:text-5xl text-text-primary mb-5 leading-tight whitespace-pre-line"
            style={displayFont}>
            {s.seating_planner_h2}
          </motion.h2>

          <motion.p variants={fadeInUp} className="text-text-secondary text-base leading-relaxed mb-6">
            {s.seating_planner_body}
          </motion.p>

          <motion.ul variants={fadeInUp} className="space-y-2.5 mb-8">
            {s.seating_planner_bullets.map((point) => (
              <li key={point} className="flex items-start gap-2.5 text-sm text-text-secondary">
                <Check size={14} className="text-primary flex-shrink-0 mt-0.5" />
                {point}
              </li>
            ))}
          </motion.ul>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-start gap-3">
            <Link
              to="/wedding-seating-planner"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
              {s.seating_planner_cta_link} <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="text-sm text-text-muted sm:self-center">£9.99 {s.seating_planner_price_label}</span>
          </motion.div>

        </motion.div>

        {/* Right: animated canvas mockup */}
        <motion.div
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.65, delay: 0.15 }}>
          <BrowserMockup contentHeight={360}>
            <HeroAnimCanvas />
          </BrowserMockup>
        </motion.div>

      </div>
    </section>
  );
}
